import { useEffect } from 'react';
import { useTranslations } from './hooks/useTranslations';
import { useGameLogic } from './hooks/useGameLogic';
import { GameHeader } from './components/GameHeader';
import { GameBoard } from './components/GameBoard';
import { GameControls } from './components/GameControls';
import { StatsModal } from './components/StatsModal';
import { AchievementsModal } from './components/AchievementsModal';
import { GameSettings } from './components/GameSettings';
import { Button } from './components/ui/button';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './components/ui/card';
import { Badge } from './components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Toaster } from 'sonner';

export default function App() {
  const game = useGameLogic();
  const t = useTranslations(game.language);

  // Expose function globally for onclick attributes
  useEffect(() => {
    (window as any).handleNewGame = game.handleNewGame;
    return () => {
      delete (window as any).handleNewGame;
    };
  }, [game.handleNewGame]);

  if (!game.puzzle) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <GameHeader
        darkMode={game.darkMode}
        language={game.language}
        soundEnabled={game.soundEnabled}
        onToggleDarkMode={game.toggleDarkMode}
        onToggleSound={game.setSoundEnabled}
        onChangeLanguage={() => game.setLanguage(game.language === 'pl' ? 'ua' : 'pl')}
        onShowStats={() => game.setShowStats(true)}
        onShowAchievements={() => game.setShowAchievements(true)}
        mobileMenuOpen={game.mobileMenuOpen}
        setMobileMenuOpen={game.setMobileMenuOpen}
        t={t}
      />

      <main className="container mx-auto p-4 max-w-4xl">
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>{game.language === 'ua' ? game.puzzle.nameUa : game.puzzle.name}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 dark:text-slate-300 mb-2">
              {game.language === 'ua' ? game.puzzle.descriptionUa : game.puzzle.description}
            </p>
            <Badge variant="outline">{game.puzzle.size}×{game.puzzle.size}</Badge>
          </CardContent>
        </Card>

        <div className="flex justify-center gap-8 mb-6 text-lg">
          <div className="text-center">
            <div className="text-sm text-slate-500">{t.time}</div>
            <div className="text-2xl font-bold font-mono">{game.formatTime(game.time)}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-slate-500">{t.score}</div>
            <div className="text-2xl font-bold text-ua-blue">{game.score}</div>
          </div>
          <div className="text-center">
            <div className="text-sm text-slate-500">{t.hints}</div>
            <div className="text-2xl font-bold text-ua-yellow">{game.hints}</div>
          </div>
        </div>

        <GameControls
          timerRunning={game.timerRunning}
          hints={game.hints}
          onStartPause={() => game.setTimerRunning(!game.timerRunning)}
          onValidate={game.handleValidate}
          onHint={game.handleHint}
          onReset={game.resetGrid}
        />

        <GameBoard
          grid={game.grid}
          puzzle={game.puzzle}
          cellSize={game.cellSize}
          clickMode={game.clickMode}
          onToggleCell={game.toggleCell}
        />

        <div className="flex justify-center gap-2 mb-6">
          <Button variant={game.clickMode === 'fill' ? 'default' : 'outline'} size="sm" onClick={() => game.setClickMode('fill')}>
            {game.language === 'pl' ? 'Zaznacz' : 'Позначити'}
          </Button>
          <Button variant={game.clickMode === 'x' ? 'default' : 'outline'} size="sm" onClick={() => game.setClickMode('x')}>
            X
          </Button>
        </div>

        <GameSettings
          difficulty={game.difficulty}
          onDifficultyChange={game.setDifficulty}
          t={t}
        />
      </main>

      <StatsModal
        open={game.showStats}
        onOpenChange={game.setShowStats}
        t={{
          stats: t.stats,
          gamesPlayed: t.gamesPlayed,
          winRate: t.winRate,
          bestTime: t.bestTime,
          hintsUsed: t.hintsUsed,
        }}
      />

      <AchievementsModal
        open={game.showAchievements}
        onOpenChange={game.setShowAchievements}
        t={{
          achievements: t.achievements,
          description: t.description,
        }}
      />

      <Dialog open={game.gameWon} onOpenChange={() => {}}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-center text-2xl text-green-600">{t.congratulations}</DialogTitle>
          </DialogHeader>
          <div className="text-center py-4">
            <p className="text-lg mb-2">{t.puzzleSolved}</p>
            <p className="text-2xl font-bold text-ua-blue mb-4">{t.score}: {game.score}</p>
            <p className="text-sm text-slate-600 mb-6">{t.time}: {game.formatTime(game.time)}</p>
            <Button onClick={game.handleNewGame}>{t.playAgain}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {game.showResult && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 animate-bounce">
          <div className={`px-6 py-3 rounded-xl shadow-lg ${game.showResult === 'correct' ? 'bg-green-100 text-green-700 border-2 border-green-500' : 'bg-red-100 text-red-700 border-2 border-red-500'}`}>
            {game.showResult === 'correct' ? t.correct : t.incorrect}
          </div>
        </div>
      )}

      <Toaster position="top-right" richColors />
    </div>
  );
}
